import React, { useEffect, useState } from 'react';
import ModalBasic from '@/components/ModalBasic';
import { validateSourceDraft, DELIMITER_OPTIONS } from './constants';

const emptyDraft = {
  name: '',
  fileName: '',
  shared: false,
  isTrigger: false,
  delimiter: null,
  pattern: '',
};

// The per-input delimiter has a third state the column mapping's does not:
// absent means "use the store's", and must persist as NULL rather than "".
const SOURCE_DELIMITER_OPTIONS = [
  { value: '__inherit__', label: 'Use the store’s delimiter' },
  ...DELIMITER_OPTIONS.filter((o) => o.value !== ''),
];

const SourceEditorModal = ({
  open,
  source,
  sources,
  steps,
  storeId,
  isFirstInput,
  onClose,
  onSubmit,
  saving,
}) => {
  const [draft, setDraft] = useState(emptyDraft);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDraft(
      source
        ? {
            name: source.name || '',
            fileName: source.fileName || '',
            shared: !!source.shared,
            isTrigger: !!source.isTrigger,
            delimiter: source.delimiter ?? null,
            pattern: source.pattern || '',
          }
        : emptyDraft
    );
    setError(null);
  }, [open, source]);

  const submit = async () => {
    const message = validateSourceDraft(draft, sources, steps, source?.id ?? null);
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    await onSubmit(draft);
  };

  const setShared = (shared) =>
    setDraft((prev) => ({
      ...prev,
      shared,
      // A shared input may not be the trigger, so don't let the form hold a
      // combination the API will reject.
      isTrigger: shared ? false : prev.isTrigger,
    }));

  return (
    <ModalBasic
      id="declared-input-editor"
      title={source ? `Edit input "${source.name}"` : 'Declare an input'}
      modalOpen={open}
      setModalOpen={onClose}
      width="2xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Each declared input is registered as its own table, named below, which the step SQL
          selects <code className="font-mono">FROM</code>. Declare inputs only for a store whose
          feed is assembled from several files — a single-file store needs none, and its one upload
          is registered as <code className="font-mono">source</code>.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Table name
            </label>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. barcodes"
              spellCheck={false}
              className="w-full px-3 py-2 font-mono text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              A plain identifier. It cannot match a transform step&rsquo;s name, even in different
              case.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {draft.shared ? 'Full key' : 'File name suffix'}
            </label>
            <input
              type="text"
              value={draft.fileName}
              onChange={(e) => setDraft({ ...draft, fileName: e.target.value })}
              placeholder={draft.shared ? '_BP_SKU/bp_sku.csv' : 'barcodes.csv'}
              spellCheck={false}
              className="w-full px-3 py-2 font-mono text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {draft.shared ? (
                <>The file&rsquo;s own fixed key within the feed bucket.</>
              ) : (
                <>
                  The suffix this store&rsquo;s timestamped uploads end in —{' '}
                  <span className="font-mono">barcodes.csv</span> matches{' '}
                  <span className="font-mono">{storeId}_20260902_1807_barcodes.csv</span>.
                </>
              )}
            </p>
          </div>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={draft.shared}
            onChange={(e) => setShared(e.target.checked)}
            className="mt-0.5 form-checkbox"
          />
          <span className="text-sm text-slate-700 dark:text-slate-300">
            Shared reference file
            <span className="block text-xs text-slate-500 dark:text-slate-400">
              Not part of this store&rsquo;s upload — fetched from its own fixed key, and typically
              read by several stores. A shared input cannot be the trigger.
            </span>
          </span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Delimiter
            </label>
            <select
              value={draft.delimiter === null ? '__inherit__' : draft.delimiter}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  delimiter: e.target.value === '__inherit__' ? null : e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              {SOURCE_DELIMITER_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Per-file override. A store&rsquo;s own uploads and its shared list often differ, which
              one store-wide value cannot express.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Pattern <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={draft.pattern}
              onChange={(e) => setDraft({ ...draft, pattern: e.target.value })}
              placeholder="e.g. .*_inv_[0-9]+\.csv"
              spellCheck={false}
              className="w-full px-3 py-2 font-mono text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Replaces suffix matching with a regexp over the whole file name, for feeds a suffix
              can&rsquo;t separate. The pipeline anchors and case-folds it itself.
            </p>
          </div>
        </div>

        {isFirstInput && !source && (
          <div className="rounded border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 p-3">
            <p className="text-sm text-sky-800 dark:text-sky-200">
              This is the store&rsquo;s first declared input, so it becomes the trigger
              automatically — a manifest with no trigger is inert. You can move the trigger once
              the other inputs exist.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3">
            <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : source ? 'Update input' : 'Declare input'}
          </button>
        </div>
      </div>
    </ModalBasic>
  );
};

export default SourceEditorModal;
